import type { WhatsNewRelease } from '../components/WhatsNewDialog';

/**
 * Release notes announced once per agent in the Selling Calculator. The
 * acknowledgement is stored server-side against their CEA, not in this browser.
 *
 * Newest first. Bump the newest `version` whenever the notes change — that string is
 * what decides whether an agent has already been shown them, so a wording fix without a
 * bump deliberately leaves everyone who has already dismissed it alone. Ticking
 * "Don't show this again" opts an agent out of every future release, not just this one.
 *
 * Keep entries in the agent's terms: what changed on screen and what to do differently,
 * not which component was refactored. Scope them to this calculator and to what every
 * reader can reach — everyone who can open this calculator can also open the Buying
 * Calculator, so the handoff to it belongs here.
 */
export const SELLING_WHATS_NEW: WhatsNewRelease[] = [
  {
    version: '1.0',
    date: 'October 2026',
    headline:
      'Selling Calculator 1.0 — everything a seller needs to know: what they walk away with, and when each milestone falls due.',
    items: [
      {
        title: 'Private and HDB',
        body:
          'Two tabs, each with its own figures, fees and timeline — hold two properties side by side without retyping.\n'
          + '• Private: option and exercise fees as a percentage of the sale price, plus Seller Stamp Duty\n'
          + '• HDB: the fixed $1,000 option and $4,000 exercise fees, HDB resale expenses and the official HDB resale timeline',
      },
      {
        title: 'Simple or Detailed',
        body:
          'A mode switch decides how much the form asks about ownership.\n'
          + '• Simple: one owner, one CPF withdrawal with accrued interest, one proceeds figure\n'
          + '• Detailed: a joint sale, with a second owner, shares and a CPF withdrawal each\n'
          + 'A second seller entered in Detailed is kept when you switch to Simple — it just sits out until you switch back.',
      },
      {
        title: 'Property and Sellers',
        body:
          '• Property Sale Price and Outstanding Loan set the ceiling\n'
          + '• Sellers: shares start at 50/50, and typing one balances the other\n'
          + '• Each owner\'s CPF withdrawal is refunded to their own OA\n'
          + '• Loading a profile fills this in — a joint profile opens two cards with the owners\' names',
      },
      {
        title: 'Expenses',
        body:
          '• Agent Service Fee: 2% of the sale price plus GST by default — tap the amount to change the rate, switch to a fixed fee, or drop GST\n'
          + '• Legal Fee, pre-filled at $3,000\n'
          + '• Bank Loan Penalty for an early redemption\n'
          + '• HDB only: Resale Application ($80) and Misc Fee',
      },
      {
        title: 'HDB settings',
        body:
          '• Resale Application Days after exercising the OTP — every later milestone shifts with it\n'
          + '• HDB Upgrading Fee still outstanding\n'
          + '• 3-Month Extension when the seller has exercised the OTP on their next property — adds 90 days to handover',
      },
      {
        title: 'Seller Stamp Duty (Private)',
        body:
          'Switch it on and enter the Purchase and Sale dates. Purchases from 4 July 2025 use the 16% / 12% / 8% / 4% four-year schedule; earlier ones keep 12% / 8% / 4%. The band applied is shown, and the amount comes off the proceeds.',
      },
      {
        title: 'Sales Proceeds and Breakdown',
        body:
          '• The headline figure is what the seller receives — or a Shortfall in red when the sale does not cover everything\n'
          + '• The breakdown shows it line by line: sale price, loan, CPF, expenses (tap to expand) and stamp duty\n'
          + '• Two sellers add a Proceeds Split card showing what each one walks away with\n'
          + 'Settlement runs in that order: loan, expenses and stamp duty come off the sale, each owner\'s CPF goes back to their own OA, then the remaining cash is divided by the shares.',
      },
      {
        title: 'Sales Schedule',
        body:
          'The milestone timeline, counted forward from the OTP date.\n'
          + '• Private: OTP → exercise → completion; tap the pencil to change the 1% / 4% option and exercise fees\n'
          + '• HDB: OTP → exercise → resale application → acceptance → endorsement → approval → completion → handover\n'
          + '• Tap the pencil beside any date to pin it — later milestones recalculate from it\n'
          + '• Show Dates hides or shows the dates, on screen and in the PDF',
      },
      {
        title: 'Carry the proceeds to the Buying Calculator',
        body:
          '• Each seller\'s cash share feeds the Sales Proceed row in Available Funds, and their CPF refund feeds Additional CPF\n'
          + '• Only sellers who are also buying carry over — tap the info icon on either row to see who was counted\n'
          + '• Selling an HDB flat also flags the EC resale levy and the second HDB loan cash set-aside',
      },
      {
        title: 'Save, History and PDF',
        body:
          '• Load Example / History reopens any saved case — history is shared across the Super Calculators\n'
          + '• Save to History / Profile: save to history only, turn the sellers into client profiles, or both linked together. With a profile loaded, one click saves and writes the names back\n'
          + '• Clear resets the calculator to its defaults\n'
          + '• Export PDF: the proceeds breakdown and full sales schedule, ready to send',
      },
      {
        title: 'Display and help',
        body:
          '• A- / A+ text size, remembered across calculators\n'
          + '• 1 Scroll / 2 Scroll on desktop — keep the proceeds in view while you adjust an input\n'
          + '• The tutorial (cap button) walks through every control, with "Show on Page" to highlight it, and a downloadable manual',
      },
    ],
  },
];
